import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, Download, Share, Plus, X, QrCode } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function BusinessCard() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [, setCanInstall] = useState(false);
  const deferredPrompt = useRef(null);
  const isIOS = typeof navigator !== "undefined" && /iP(hone|ad|od)/.test(navigator.userAgent);
  const isStandalone = typeof window !== "undefined" && (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone);

  useEffect(() => {
    api
      .get(`/affiliates/card/${code}`)
      .then((r) => {
        setData(r.data);
        document.title = "MyAZbnb.com — Scan to Book";
      })
      .catch(() => setError(true));
  }, [code]);

  // Point the PWA manifest + apple metadata at THIS card so it installs as its own app with the logo icon.
  useEffect(() => {
    const head = document.head;
    const prevManifest = document.querySelector('link[rel="manifest"]');
    const prevHref = prevManifest ? prevManifest.getAttribute("href") : null;
    const cardManifest = `${BACKEND}/api/affiliates/card/${code}/manifest.webmanifest`;
    if (prevManifest) prevManifest.setAttribute("href", cardManifest);
    else {
      const l = document.createElement("link");
      l.rel = "manifest";
      l.href = cardManifest;
      head.appendChild(l);
    }
    const titleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const prevTitle = titleMeta ? titleMeta.getAttribute("content") : null;
    if (titleMeta) titleMeta.setAttribute("content", "MyAZbnb.com");

    const onBeforeInstall = (e) => {
      e.preventDefault();
      deferredPrompt.current = e;
      setCanInstall(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      if (prevManifest && prevHref) prevManifest.setAttribute("href", prevHref);
      if (titleMeta && prevTitle) titleMeta.setAttribute("content", prevTitle);
    };
  }, [code]);

  const addToHomeScreen = async () => {
    if (deferredPrompt.current) {
      deferredPrompt.current.prompt();
      await deferredPrompt.current.userChoice;
      deferredPrompt.current = null;
      setCanInstall(false);
      return;
    }
    setShowHelp(true);
  };

  const download = async () => {
    try {
      const res = await fetch(data.card_image_url);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `myazbnb-card-${code}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      window.open(data.card_image_url, "_blank");
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#1f2a24] flex flex-col items-center justify-center text-white p-8 text-center">
        <QrCode className="h-10 w-10 mb-4 opacity-60" />
        <h1 className="font-serif text-2xl mb-2">Card not found</h1>
        <p className="text-white/70 text-sm">This referral card link isn't valid. Please check with the host.</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#1f2a24] flex items-center justify-center text-white">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1f2a24] flex flex-col items-center justify-center px-4 py-8" data-testid="business-card-page">
      <div className="w-full max-w-md">
        <img
          src={data.card_image_url}
          alt={`${data.property_name} — scan to book`}
          className="w-full rounded-2xl shadow-2xl"
          data-testid="business-card-image"
        />

        <p className="text-center text-white/70 text-sm mt-5">
          Hold up this card so anyone can <strong className="text-white">scan the QR code and book</strong>.
        </p>

        {!isStandalone && (
          <div className="flex flex-col gap-3 mt-5">
            <button
              onClick={addToHomeScreen}
              data-testid="add-home-screen-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#C05A45] hover:bg-[#a94a37] transition-colors text-white font-semibold py-3.5 rounded-full"
            >
              <Plus className="h-5 w-5" /> Add card to my home screen
            </button>
            <button
              onClick={download}
              data-testid="download-card-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 transition-colors text-white font-medium py-3 rounded-full border border-white/20"
            >
              <Download className="h-5 w-5" /> Download card image
            </button>
          </div>
        )}

        <p className="text-center text-white/40 text-xs mt-6">Referral code: {data.code}</p>
      </div>

      {showHelp && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4" onClick={() => setShowHelp(false)}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="add-home-help">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl text-[#2C3E35]">Add card to home screen</h2>
              <button onClick={() => setShowHelp(false)} data-testid="close-help"><X className="h-5 w-5 text-gray-400" /></button>
            </div>
            <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-[#FAF9F6] border border-[#E7E3DC]">
              <img src="/icon-192.png" alt="App icon" className="h-12 w-12 rounded-xl" />
              <div>
                <p className="text-sm font-semibold text-[#2C3E35]">MyAZbnb.com</p>
                <p className="text-xs text-gray-500">This is how it'll look on your phone</p>
              </div>
            </div>
            {isIOS ? (
              <ol className="space-y-3 text-sm text-[#2C3E35]">
                <li className="flex gap-2"><span className="font-bold text-[#C05A45]">1.</span> Tap the <Share className="inline h-4 w-4 mx-0.5" /> <strong>Share</strong> button at the bottom of Safari.</li>
                <li className="flex gap-2"><span className="font-bold text-[#C05A45]">2.</span> Scroll down and tap <strong>“Add to Home Screen”</strong>.</li>
                <li className="flex gap-2"><span className="font-bold text-[#C05A45]">3.</span> Tap <strong>Add</strong> — the MyAZbnb.com icon appears on your home screen.</li>
              </ol>
            ) : (
              <ol className="space-y-3 text-sm text-[#2C3E35]">
                <li className="flex gap-2"><span className="font-bold text-[#C05A45]">1.</span> Tap the <strong>⋮</strong> menu (top-right) in Chrome.</li>
                <li className="flex gap-2"><span className="font-bold text-[#C05A45]">2.</span> Tap <strong>“Add to Home screen”</strong> (or <strong>Install app</strong>).</li>
                <li className="flex gap-2"><span className="font-bold text-[#C05A45]">3.</span> Tap <strong>Add</strong> — the MyAZbnb.com icon appears on your home screen.</li>
              </ol>
            )}
            <button onClick={() => setShowHelp(false)} className="mt-5 w-full bg-[#2C3E35] text-white py-3 rounded-full font-medium" data-testid="help-got-it">Got it</button>
          </div>
        </div>
      )}
    </div>
  );
}
